import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowLeft, Play, Check, Lock, Share2,
  ExternalLink, AlertTriangle, Wrench,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { getIcon } from "@/lib/icons";
import { toast } from "sonner";

const TILE = {
  lime: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
  cyber: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  tangerine: "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
};
const tile = (c) => TILE[c] || TILE.cyber;

function LessonRow({ lesson, isNext, onOpen }) {
  const completed = lesson.completed;
  const locked = lesson.locked;
  const base = completed
    ? "border-emerald-200/70 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10"
    : isNext
    ? "border-border bg-card shadow-md ring-1 ring-primary/30"
    : locked
    ? "border-border bg-secondary/50 opacity-70"
    : "border-border bg-card shadow-sm hover:shadow-md hover:border-primary/30";
  return (
    <button
      data-testid={`tool-lesson-${lesson.slug}`}
      disabled={locked}
      onClick={() => !locked && onOpen(lesson.video_id)}
      className={`relative w-full h-full text-left rounded-2xl border p-4 transition-all ${base} ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className="flex items-center gap-4">
        <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-xl ${completed ? "bg-emerald-500 text-white" : isNext ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
          {completed ? <Check className="h-5 w-5" /> : locked ? <Lock className="h-4 w-4" /> : <Play className="h-4 w-4 fill-current" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{lesson.step}</span>
            {isNext && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Next up</span>}
            {!locked && lesson.is_fallback && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">Related · best match curating</span>
            )}
          </div>
          <h4 className="font-heading font-bold text-sm md:text-base truncate mt-1.5">{lesson.title}</h4>
          {lesson.video_title ? (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{lesson.channel} · {lesson.duration_label}</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-0.5">Curating the best lesson — unlocks soon</p>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ToolCourse() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toolsParam = slug || searchParams.get("tools") || "";
  const tools = toolsParam.split(",").map((s) => s.trim()).filter(Boolean);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.post("/tools/course", { tools });
      setCourse(data);
      const hasLocked = (data.groups || []).some((g) => g.lessons.some((l) => l.locked));
      if (hasLocked && pollRef.current < 5) {
        pollRef.current += 1;
        setTimeout(load, 10000);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 400 || e.response?.status === 404) navigate("/tools", { replace: true });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolsParam]);

  useEffect(() => {
    if (tools.length === 0) { navigate("/tools", { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolsParam]);

  const openLesson = (videoId) => { if (videoId) navigate(`/lesson/${videoId}`); };

  const share = async () => {
    const url = `${window.location.origin}/tools/course?tools=${tools.join(",")}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied! 🔗");
    } catch {
      toast.message(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          <p className="mt-4 font-heading font-semibold text-muted-foreground">Building your tool course…</p>
        </div>
      </div>
    );
  }
  if (!course) return null;

  const headline = course.tools.map((t) => t.name).join(" + ");

  return (
    <div className="min-h-screen bg-background" data-testid="tool-course">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <button data-testid="tool-course-back" onClick={() => navigate("/tools")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-5">
          <ArrowLeft className="h-4 w-4" /> All tools
        </button>

        {/* header */}
        <div className="rounded-3xl border border-border bg-card shadow-sm p-6 md:p-8" data-testid="tool-course-header">
          <div className="flex items-center gap-2 flex-wrap">
            {course.tools.map((t) => {
              const Icon = getIcon(t.icon);
              return (
                <span key={t.slug} className={`grid place-items-center h-11 w-11 rounded-xl ${tile(t.color)}`}>
                  <Icon className="h-5 w-5" />
                </span>
              );
            })}
          </div>
          <p className="mt-4 text-xs font-bold tracking-[0.15em] uppercase text-primary inline-flex items-center gap-1.5" data-testid="tool-course-eyebrow">
            <Wrench className="h-3.5 w-3.5" /> Complete course · basics to real projects
          </p>
          <h1 className="mt-2 font-heading font-bold tracking-tight text-3xl md:text-4xl leading-tight">
            Learn {headline}
          </h1>
          <p className="mt-2 text-muted-foreground">{course.tools[0]?.tagline}</p>

          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{course.language_label}</span>
            {course.creator_pref && course.creator_pref !== "both" && (
              <span data-testid="tool-creator-chip" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">
                {course.creator_pref === "indian" ? "Indian creators" : "Global creators"}
              </span>
            )}
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">{course.lesson_count} lessons</span>
            {course.tools[0]?.official_url && (
              <a href={course.tools[0].official_url} target="_blank" rel="noreferrer" data-testid="tool-official-link" className="text-xs font-semibold px-2.5 py-1 rounded-full border border-border bg-background hover:bg-accent transition-colors inline-flex items-center gap-1">
                Official site <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <button data-testid="tool-course-share" onClick={share} className="text-xs font-semibold px-2.5 py-1 rounded-full border border-border bg-background hover:bg-accent transition-colors inline-flex items-center gap-1">
              <Share2 className="h-3 w-3" /> Share
            </button>
          </div>

          {course.total > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm font-semibold mb-2">
                <span data-testid="tool-course-percent">{course.percent}% complete</span>
                <span className="text-muted-foreground">{course.completed_count}/{course.total}</span>
              </div>
              <div className="h-3 rounded-full bg-secondary overflow-hidden">
                <motion.div className="h-full rounded-full bg-primary" initial={{ width: 0 }} animate={{ width: `${course.percent}%` }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} />
              </div>
            </div>
          )}

          <div className="mt-6">
            <Button data-testid="tool-course-start" onClick={() => openLesson(course.next_video_id)} disabled={!course.next_video_id} className="bg-primary text-primary-foreground">
              <Play className="h-4 w-4 fill-current" /> {course.completed_count > 0 ? "Continue learning" : "Start first lesson"}
            </Button>
          </div>
        </div>

        {/* limited content banner */}
        {course.limited_content && (
          <div className="mt-6 rounded-2xl border border-orange-200/70 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 p-4 flex items-start gap-3" data-testid="tool-course-limited-banner">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-orange-600 dark:text-orange-400" />
            <p className="text-sm text-foreground/85">
              <span className="font-semibold text-foreground">Early access — </span>
              this is a fast-moving tool, so some lessons are still being curated. We only show genuinely good, up-to-date videos — locked lessons unlock automatically as we find the best ones.
            </p>
          </div>
        )}

        {/* groups */}
        <div className="mt-10 space-y-10">
          {course.groups.map((g) => (
            <section key={g.tool_slug} data-testid={`tool-group-${g.tool_slug}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`grid place-items-center h-9 w-9 rounded-xl ${tile(g.color)}`}>
                  {(() => { const I = getIcon(g.icon); return <I className="h-4 w-4" />; })()}
                </span>
                <h2 className="font-heading font-bold tracking-tight text-xl md:text-2xl">{g.tool_name}</h2>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {g.lessons.map((l) => (
                  <LessonRow key={l.slug} lesson={l} isNext={l.video_id && l.video_id === course.next_video_id} onOpen={openLesson} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </PageTransition>
    </div>
  );
}
